import {
  collectFinishedFeedEntries,
  collectHomeFeedEntries,
  ownedEntriesEqual,
} from "@/campaigns";

import { Fragment } from "react";
import { campaignKey } from "@/campaigns/campaign-key";
import { cn } from "@/lib/utils";
import { useAppStore } from "@/stores";
import { useStoreWithEqualityFn } from "zustand/traditional";

/**
 * The mixed active-campaign feed: every type together, type badge on the card
 * (no per-type sections). Grouped by type in registration order, ending-soonest
 * first within each type (see `collectHomeFeedEntries`).
 *
 * The feed owns the layout, the modules own the cards:
 * - desktop: 1 campaign → full-width featured card; exactly 2 → one 2-up row
 *   (a single tall row always fits); 3+ → always 3 per row, cards filling
 *   their cells edge-to-edge — the column count is what keeps the uncropped
 *   2:1 art short enough for two rows inside the panel.
 * - mobile: always one card per row (no featured promotion — a stacked card is
 *   already full-width).
 */
export const CampaignFeed = ({
  variant,
}: {
  variant: "desktop" | "mobile";
}) => {
  // Equality fn required: the collector allocates per call, and entries are
  // stable store refs — see ownedEntriesEqual.
  const entries = useStoreWithEqualityFn(
    useAppStore,
    collectHomeFeedEntries,
    ownedEntriesEqual
  );

  if (entries.length === 0) return null;

  if (variant === "desktop" && entries.length === 1) {
    const { module, entry } = entries[0];
    return (
      <div className="w-full p-4">{module.homeCard(entry, "featured")}</div>
    );
  }

  return (
    <div
      className={cn(
        "grid w-full gap-4 p-4",
        // Cards fill their cells edge-to-edge (no width cap, no slack): the
        // COLUMN COUNT keeps the uncropped 2:1 art short enough for two rows.
        // 2 campaigns → one 2-up row (a single tall row always fits); 3+ →
        // always 3-up (~265px cards — two rows fit the panel comfortably).
        // Non-multiples of 3 leave a short last row, the standard grid ragged
        // edge — content-end space, not slack carved into the middle.
        variant === "mobile"
          ? "grid-cols-1"
          : entries.length === 2
            ? "grid-cols-2"
            : "grid-cols-3"
      )}
    >
      {entries.map(({ module, entry }) => (
        <Fragment key={`${module.id}:${campaignKey(entry.campaign)}`}>
          {module.homeCard(entry, "grid")}
        </Fragment>
      ))}
    </div>
  );
};

// A short results history is laid out as one row: up to 5 entries render 5-up on
// desktop (uniform ⅕-width cards, no 4+1 stub row). Longer feeds drop to 4-up
// and wrap — the backend caps the history at FINISHED_CAMPAIGNS_VISIBLE_LIMIT
// (10), so at most 4+4+2 — where the wider cards carry the extra rows better.
const FIVE_COLUMN_THRESHOLD = 5;

/**
 * The mixed finished/results grid: every type together in ONE grid (¼-width
 * cards on desktop, halves on mobile), most-recently-ended first within each
 * type. Each module renders its own compact greyed result card.
 */
export const FinishedCampaignFeed = () => {
  const entries = useStoreWithEqualityFn(
    useAppStore,
    collectFinishedFeedEntries,
    ownedEntriesEqual
  );

  if (entries.length === 0) return null;

  return (
    <div
      className={cn(
        "grid w-full grid-cols-2 gap-4 p-4 md:grid-cols-4",
        entries.length <= FIVE_COLUMN_THRESHOLD && "md:grid-cols-5"
      )}
    >
      {entries.map(({ module, entry }) => (
        <Fragment key={`${module.id}:${campaignKey(entry.campaign)}`}>
          {module.finishedCard(entry)}
        </Fragment>
      ))}
    </div>
  );
};
